import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { IS_ADMIN_BUILD } from '@/config/appVariant';
import { createAuthSlice, type AuthSlice } from './slices/authSlice';
import { createMarketSlice, type MarketSlice } from './slices/marketSlice';
import { createUISlice, type UISlice } from './slices/uiSlice';
import { createChainSlice, type ChainSlice } from './slices/chainSlice';

/**
 * Combined store type
 */
export type StoreState = AuthSlice & MarketSlice & UISlice & ChainSlice;

/**
 * Create the main application store
 */
export const useStore = create<StoreState>()(
  devtools(
    subscribeWithSelector(
      persist(
        (...a) => ({
          ...createAuthSlice(...a),
          ...createMarketSlice(...a),
          ...createUISlice(...a),
          ...createChainSlice(...a),
        }),
        {
          name: 'alpha-storage',
          partialize: (state) => ({
            // Only persist UI state (auth is handled manually in App.tsx)
            ui: {
              theme: state.ui.theme,
              sidebarCollapsed: state.ui.sidebarCollapsed,
              tradingActivityTimezone: state.ui.tradingActivityTimezone,
            },
            chain: {
              selectedChain: state.chain.selectedChain,
            },
          }),
          merge: (persistedState, currentState) => {
            // Deep merge per-slice to preserve functions while restoring persisted values
            const persisted = persistedState as Partial<StoreState> | undefined;
            return {
              ...currentState,
              ui: { ...currentState.ui, ...persisted?.ui },
              chain: { ...currentState.chain, ...persisted?.chain },
            };
          },
        }
      )
    ),
    {
      name: 'Alpha Store',
    }
  )
);

/**
 * Convenience hooks for accessing specific slices
 */
export const useAuth = () => useStore((state) => state.auth);
export const useMarket = () => useStore((state) => state.market);
export const useUI = () => useStore((state) => state.ui);
export const useChain = () => useStore((state) => state.chain);

/**
 * Selector hooks for specific values (better performance)
 */
export const useIsAuthenticated = () => useStore((state) => state.auth.isAuthenticated);
export const useUser = () => useStore((state) => state.auth.user);
/** Server-authoritative admin flag from the login/me payload. */
export const useIsAdmin = () => useStore((state) => state.auth.user?.admin ?? false);
/**
 * Whether to render admin UI: requires the admin BUILD and the server admin
 * flag. Always false in the general-user build (IS_ADMIN_BUILD is a build-time
 * constant), and false in the admin build for non-admin accounts.
 */
export const useAdminUI = () => {
  const isAdmin = useIsAdmin();
  return IS_ADMIN_BUILD && isAdmin;
};
export const useBetaDisclaimer = () => useStore((state) => state.auth.betaDisclaimer);
export const useTheme = () => useStore((state) => state.ui.theme);
export const useSidebarCollapsed = () => useStore((state) => state.ui.sidebarCollapsed);
export const useTradingActivityTimezone = () => useStore((state) => state.ui.tradingActivityTimezone);
export const useSelectedSymbol = () => useStore((state) => state.market.selectedSymbol);
export const useTickers = () => useStore((state) => state.market.tickers);
